import {useMutation, useMutationState, useQuery, useQueryClient} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {LENS_AGENT_WORKFLOWS} from 'app/utils/constants';

import {AgentResponse, parseAgentResponse} from './utils';

export type AgentMethod = {
  name: string;
  description: string;
  objectives: string[];
};

export type ApiAgentWorkflow = {
  id: string;
  name: string;
  description: string;
  featureIds: number[];
  objectives: string[];
  dateRange: any[];
  cadence: string;
  runs: AgentRunWithoutMessages[];
};

interface AgentRunBaseType {
  status: 'pending' | 'in_progress' | 'completed' | 'failed'; // For now probably only completed and failed
  startedAt: string; // ISO8601 timestamp
  endedAt: string | null; // ISO8601 timestamp or null if not ended yet
  userId: string;
  runId: string;
  workflowId: string;
  featureIds: number[];
}

export interface ApiAgentRun extends AgentRunBaseType {
  messages: AgentResponse[]; // Array of messages or steps in the run
}
export interface AgentRunWithoutMessages extends AgentRunBaseType {
  //computed in API
  numFindings: number | undefined;
}

const DEFAULT_SETUP_JOB_RESPONSE: AgentResponse[] = [
  {
    type: 'step',
    label: 'Setting up the agent',
    step: 'Setting up the agent',
  },
  {
    type: 'text',
    label: 'Configuring the model for your use case...',
    step: 'Setting up the agent',
  },
];

// Custom hook for agent mutations
export const useAgentMutation = () => {
  const mutationKey = ['agent-run'];
  const queryClient = useQueryClient();

  // Store streaming data per feature
  const [featureStreams, setFeatureStreams] = React.useState<Record<number, AgentResponse[]>>({});

  // Use TanStack Query mutation for streaming
  const agentMutation = useMutation({
    mutationKey,
    mutationFn: async ({
      featureId,
      objectives: selectedObjectives,
      runId,
    }: {
      featureId: number;
      objectives: string[];
      runId: string;
    }) => {
      console.info(`Processing feature ${featureId}`);

      // Initialize the stream with default responses (but maybe we can just do
      // an empty array?)
      setFeatureStreams((prev) => ({
        ...prev,
        [featureId]: [...DEFAULT_SETUP_JOB_RESPONSE],
      }));

      const chatStream = api.agent.sendMessage(featureId, selectedObjectives, runId);

      for await (const chunk of chatStream) {
        const streamedResponse = parseAgentResponse(chunk);
        setFeatureStreams((prev) => ({
          ...prev,
          [featureId]: [...(prev[featureId] || []), ...streamedResponse],
        }));
      }

      console.info(`Completed processing feature ${featureId}`);
      return {featureId, status: 'completed'};
    },
    onMutate: (variables) => {
      const featureId = variables.featureId;
      console.info(`Starting mutation for feature ${featureId}`);
    },
    onSuccess: (data) => {
      console.info(`Successfully completed feature ${data.featureId}`);
    },
    onError: (error, variables) => {
      const featureId = variables.featureId;
      console.error(`Error processing feature ${featureId}:`, error);
    },
  });

  // Use useMutationState to track all agent mutations
  const allMutations = useMutationState({
    filters: {mutationKey},
    select: (mutation) => ({
      status: mutation.state.status,
      variables: mutation.state.variables,
      data: mutation.state.data,
      error: mutation.state.error,
      submittedAt: mutation.state.submittedAt,
    }),
  });

  // Derived state from mutation state - handle multiple concurrent mutations
  const pendingMutations = allMutations.filter((m) => m.status === 'pending');
  const successfulMutations = allMutations.filter((m) => m.status === 'success');
  const errorMutations = allMutations.filter((m) => m.status === 'error');

  const isStreaming = pendingMutations.length > 0;
  const runSucceeded =
    allMutations.length > 0 && pendingMutations.length === 0 && errorMutations.length === 0;
  const runCompleted = allMutations.length > 0 && pendingMutations.length === 0;

  // Debug logging
  React.useEffect(() => {
    if (allMutations.length > 0) {
      console.info('Mutation state update:', {
        totalMutations: allMutations.length,
        pending: pendingMutations.length,
        successful: successfulMutations.length,
        errors: errorMutations.length,
        isStreaming,
        runSucceeded,
        runCompleted,
        availableStreams: Object.keys(featureStreams),
      });
    }
  }, [
    allMutations.length,
    pendingMutations.length,
    successfulMutations.length,
    errorMutations.length,
    isStreaming,
    runSucceeded,
    runCompleted,
    featureStreams,
  ]);

  // Function to get the stream data for a specific featureId
  const getStreamForFeature = (featureId: number): AgentResponse[] => {
    return featureStreams[featureId] || [];
  };

  // Function to clear all agent data
  const clearAll = () => {
    console.info('Clearing all agent data');
    // Clear individual mutation instance
    agentMutation.reset();
    // Clear all mutations from the mutation cache
    queryClient.getMutationCache().clear();
    // Clear stream data
    setFeatureStreams({});
  };

  return {
    agentMutation,
    allMutations,
    successfulMutations,
    errorMutations,
    isStreaming,
    runSucceeded,
    runCompleted,
    getStreamForFeature,
    clearAll,
  };
};

export const AGENT_TAG_ID = 'fJ9VpUYBPT9GLpUDgTatb6';
// Using RQ here because there is a bug the context-org-id is not being set correctly on outgoing requests on load, so hacking in some default retry behavior.
export const useFeaturesQuery = (fcId: number) => {
  return useQuery({
    queryKey: ['agent-features', fcId],
    queryFn: async () => {
      const apiFeatures: I.ImmutableListOf<ApiFeature> = (
        await api.featureCollections.features(fcId).list()
      ).get('data');
      return apiFeatures.toJS() as ApiFeature[];
    },
    // Filter out features that don't have the agent tag for demo
    select: (data) => {
      const filteredData = data.filter(
        (feature) => !!feature.properties['__app']?.tagIds?.[AGENT_TAG_ID]
      );
      return filteredData;
    },
  });
};

export const useAgentRunQuery = (workflowId: string, runId: string) => {
  return useQuery({
    queryKey: ['agent-run-details', workflowId, runId],
    queryFn: async () => {
      const run = await api.agent.getRun(workflowId, runId);
      return run.toJS() as ApiAgentRun;
    },
    enabled: !!runId,
  });
};

// Get workflows for the agent based on organization ID
export const useAgentWorkflows = (orgId: string) => {
  return useQuery({
    queryKey: ['agent-workflows', orgId],
    queryFn: async () => {
      // Return the workflows for the given orgId from the constant
      return LENS_AGENT_WORKFLOWS[orgId as keyof typeof LENS_AGENT_WORKFLOWS] as ApiAgentWorkflow[];
    },
  });
};
